import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LoginService } from '@ebw-monorepo/login';
import { transform } from 'ol/proj';
import { BehaviorSubject, catchError, map, throwError } from 'rxjs';
import { IGroupButton, IMapTable, IMapTableData } from './../interfaces/mapTable.interface';
import { API_URLS, ApiUrlService } from '../../../../utils/src/lib/services/api-url.service';
import { ConfigSidebarService } from './config-sidebar.service';
import { FeatureSelectService } from './feature-select.service';

@Injectable({
  providedIn: 'root'
})
export class MapTableService {
  private mapTableConfig: BehaviorSubject<IMapTable> = new BehaviorSubject<IMapTable>(null);

  constructor(
    private _loginService: LoginService,
    private _http: HttpClient,
    private _configSidebarService: ConfigSidebarService,
    private _featureSelectService: FeatureSelectService,
    private _apiUrlService: ApiUrlService
  ) {}

  setMapTableConfig(config: IMapTable): void {
    this.mapTableConfig.next(config);
  }

  getMapTableConfig() {
    return this.mapTableConfig;
  }
  /** Funzione che mi estrae l'array di ButtonGroups*/
  createMapButtonsGroup(data: IMapTableData[]): IGroupButton[] {
    let buttons = data
      .filter((d) => !d.hide)
      .map((f) => {
        let icon = f.icon.type == 'svg' ? f.icon.path : f.icon.name;
        return { label: f.tab_name, name: f.tab_name, icon: icon, isSvg: f.icon.type == 'svg' };
      });
    return buttons;
  }

  /**
   * Function to format the data coming from the IMapTableData according to the configuration
   *
   * @param {IMapTableData} mapData data containing the information to format
   * @param {boolean} removeArrays turn arrays in the properties into strings (PDF)
   * @param {boolean} exportObject export the result as an object with a property containing the formatted data (Excel)
   * @param {string | null} projection Specified projection to conver the result into
   * @returns an object where the properties are the key-value pairs of the formatted parameter names and the data content
   */
  formatExportData(mapData: IMapTableData, removeArrays: boolean = false, exportObject: boolean = false, projection: string = null) {
    var featureConfig = null;
    // object containing information about the fields
    // anchored, bounded regex — no catastrophic backtracking, not ReDoS-prone
    // nosemgrep: nodejs_scan.javascript-dos-rule-regex_dos
    if (/Z\d+$/.test(mapData?.data[0]?.id)) {
      featureConfig = this._featureSelectService.getConfigDataFromUrn(mapData.data[0].id);
    }
    // object containing the export configuration
    const config = this._configSidebarService.getExportSettings();

    const columnsToHide = config.columnsToHide.split(';');

    const formattedData = mapData.data.map((data) => {
      const properties = data.properties;

      // if the removeArrays flag was set (for PDF), turn all arrays into strings
      if (removeArrays) {
        Object.keys(properties).forEach((key) => {
          if (!Array.isArray(properties[key])) {
            properties[key] === null ? (properties[key] = '') : properties[key];
          } else {
            properties[key] = properties[key].join(',');
          }
        });
      }

      // start with an empty object
      const obj = {};

      // if the id column is not to exclude, set it to the fullId or just the last numbers, depending on the configuration
      if (!columnsToHide.includes('id')) {
        if (config.fullId == 'true' || !/Z[0-9][0-9]*$/.test(data.id)) {
          obj['id'] = data.id;
        } else {
          obj['id'] = data.id.split('Z')?.slice(-1)[0];
        }
      }

      if (data.geometry) {
        // if the object is a point, extract the coordinates
        if (data.geometry.type === 'Point') {
          // convert the coordinates into the reference system set in the configuration and flip the coordinates if necessary
          if (projection) {
            if (projection === 'EPSG:3857') {
              Object.assign(obj, {
                X: `${data.geometry.coordinates[config.flippedCoords == 'true' ? 1 : 0]}`,
                Y: `${data.geometry.coordinates[config.flippedCoords == 'true' ? 0 : 1]}`
              });
            } else {
              const transformedCoords = transform(data.geometry.coordinates, 'EPSG:3857', projection);
              Object.assign(obj, {
                X: `${transformedCoords[config.flippedCoords == 'true' ? 1 : 0]}`,
                Y: `${transformedCoords[config.flippedCoords == 'true' ? 0 : 1]}`
              });
            }
          } else {
            // convert the coordinates into the reference system set in the configuration and flip the coordinates if necessary
            if (config.geometryType == 'EPSG:3857') {
              Object.assign(obj, {
                X: `${data.geometry.coordinates[config.flippedCoords == 'true' ? 1 : 0]}`,
                Y: `${data.geometry.coordinates[config.flippedCoords == 'true' ? 0 : 1]}`
              });
            } else {
              const transformedCoords = transform(data.geometry.coordinates, 'EPSG:3857', config.geometryType);
              Object.assign(obj, {
                X: `${transformedCoords[config.flippedCoords == 'true' ? 1 : 0]}`,
                Y: `${transformedCoords[config.flippedCoords == 'true' ? 0 : 1]}`
              });
            }
          }
        }
      }

      // iterate through all the properties of the data
      for (const property of Object.keys(properties)) {
        // process the property only if it's not in the list of columns to hide in configuration
        if (!columnsToHide.includes(property)) {
          let outerName = property;

          // change the property name with the outer name (if exists and if set in configuration)
          if (config.outerLabels == 'true' && /Z[0-9][0-9]*$/.test(data.id)) {
            const fieldConfig = featureConfig.config_collection.fields.find((field) => {
              return property == field.name;
            });

            if (fieldConfig) {
              outerName = fieldConfig.external_name;
            }
          }

          // append the new property to the object
          obj[outerName] = properties[property];
        }
      }

      // return the formatted object
      return obj;
    });

    let output: any = formattedData;

    // if the exportObject flag is set, turn the output object into an object containing the data as a property
    if (exportObject) {
      output = {};
      output[mapData.tab_name] = formattedData;
    }
    return output;
  }

  downloadAttachment(link: string) {
    const url = this._apiUrlService.createUrl(API_URLS.SMART_COMPONENT.GET_ATTACHMENTS);
    let headers = new HttpHeaders({
      Authorization: 'Bearer ' + this._loginService.getToken()
    });

    return this._http.get(`${url}?link=${link}`, { headers, responseType: 'blob' }).pipe(
      map((blob) => {
        var urlCreator = window.URL;
        return urlCreator.createObjectURL(blob);
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }
}
